import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import fleetApi from '../../services/api.service';
import { buildUrlWithParams } from '../../utils/url.utils';

const normalizePackaging = (item) => ({
  ...item,
  is_active: item.is_active === 1 || item.is_active === true,
});

export default function useGetPackaging(filters, delay = 300) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceTimeout = useRef(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const url = buildUrlWithParams('/packaging', {
        limit: filters.limit,
        offset: filters.offset,
        query: filters.query || undefined,
      });
      const response = await fleetApi.get(url, { signal: controller.signal });

      if (!controller.signal.aborted) {
        setData({
          ...response.data,
          items: (response.data?.items ?? []).map(normalizePackaging),
        });
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Une erreur est survenue'
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [filters.limit, filters.offset, filters.query]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchData();
    }, delay);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData, delay]);

  const mutate = useMemo(() => fetchData, [fetchData]);

  return { data, isLoading, error, mutate };
}
